import { stringFormat, T } from 'src/shared/helpers';
import {
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerAttributeType,
    ComposerEcommerceAttribute,
    ComposerOptionActionNode,
    ComposerOptionActionsType,
    GetActionTypeFromValue
} from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';

export function getParentGuid(stepGuid?: string | null, groupGuid?: string | null): string | undefined {
    if (stepGuid && !guidIsEmpty(stepGuid)) {
        return stepGuid;
    }
    if (groupGuid && !guidIsEmpty(groupGuid)) {
        return groupGuid;
    }
    return undefined;
}

export function getParentIDNumeric(stepID?: number, groupID?: number): number {
    if (stepID && stepID > 0) {
        return stepID;
    }
    if (groupID && groupID > 0) {
        return groupID;
    }
    return -1;
}

export function getParentType(stepGuid?: string | null, groupGuid?: string | null): ComposerAttributeType | undefined {
    if (stepGuid && !guidIsEmpty(stepGuid)) {
        return ComposerAttributeType.AttributeStep;
    }
    if (groupGuid && !guidIsEmpty(groupGuid)) {
        return ComposerAttributeType.AttributeGroup;
    }
    return undefined;
}

export function guidIsEmpty(guid?: string | null): boolean {
    return !guid || guid === Zakeke.getEmptyUUID() || guid === '00000000-0000-0000-0000-000000000000';
}

export function sortByDisplay<T extends ComposerAttributeNode | ComposerAttributeOptionNode | ComposerOptionActionNode>(nodes: T[], a: T, b: T): number {
    if (a.displayOrder === b.displayOrder) {
        return (nodes as T[]).indexOf(a) - (nodes as T[]).indexOf(b);
    }
    return a.displayOrder - b.displayOrder;
}

export function randomCode(prefix: string) {
    return `${prefix}_${Math.random().toString(36).substr(2, 6)}`;
}

// 1. find highest display order
// 2. sum highest with the total number of nodes call new values as K
// 3. assign display order to the new node as K - 1 descending, based on the current display order
// if nodes have the same display order, sort by index in the array
export function adjustDisplayOrder<T extends ComposerAttributeNode | ComposerAttributeOptionNode | ComposerOptionActionNode>(nodes: T[]): T[] {
    const maxDisplayOrder = Math.max(...nodes.map((node) => node.displayOrder), 0);
    const totalNodes = nodes.length;
    const K = maxDisplayOrder + totalNodes;
    //first sort by display order, then by index in the array
    const orderedNodes = nodes.sort((a, b) => {
        return sortByDisplay(nodes, a, b);
    });
    console.log('adjustDisplayOrder', nodes, maxDisplayOrder, totalNodes, K, orderedNodes);
    //assign to each node a new display order
    return orderedNodes.map((node, index) => ({
        ...node,
        displayOrder: K - index - 1
    }));
}

export function getCapitalizedFormattedName(name: string): string {
    // replace _ with spaces
    return name.toLowerCase().charAt(0).toUpperCase() + name.replace(/_/g, ' ').toLowerCase().slice(1);
}

// IL NOME DI UN CLONE È «‹nome› copy» (prototipo, come nello Scene Editor): poi «copy 2», «copy 3». Il codice
// resta quello di createSecureClonedName: è un identificatore, e lì «_clone_N» va bene.
export function createCopyName(currentName: string, namesToCheck: string[]): string {
    const base = stringFormat(T._('{0} copy', 'Admin'), currentName);
    const taken = new Set(namesToCheck);
    if (!taken.has(base)) return base;
    let n = 2;
    while (taken.has(`${base} ${n}`)) n++;
    return `${base} ${n}`;
}

//search for clone_N in the current names
// replace N with the next number based of the others names
export function createSecureClonedName(currentName: string, namesToCheck: string[]): string {
    const regex = /_clone_(\d+)$/;
    const match = currentName.match(regex);
    let baseName = currentName.replace(regex, '');
    let maxCloneNumber = 0;

    if (match) {
        maxCloneNumber = parseInt(match[1], 10);
    }

    namesToCheck.forEach((name) => {
        const cloneMatch = name.match(regex);
        if (cloneMatch) {
            const cloneNumber = parseInt(cloneMatch[1], 10);
            if (cloneNumber > maxCloneNumber) {
                maxCloneNumber = cloneNumber;
            }
        }
    });

    return `${baseName}_clone_${maxCloneNumber + 1}`;
}

export function tryParsePlatformValue(value: string | undefined | null): ComposerEcommerceAttribute | undefined {
    if (!value) return undefined;
    const serializedValue = value.trim();
    if (!serializedValue.startsWith('{')) return undefined;

    try {
        const parsedValue = JSON.parse(serializedValue);
        if (parsedValue && typeof parsedValue === 'object') {
            return {
                id: parsedValue.id || '',
                label: parsedValue.label || ''
            };
        }
    } catch {}
    return undefined;
}

export function getRetargetObjectsAndMaterials(nodes: ComposerOptionActionNode[]): { objectIds: string[]; materialIds: string[] } {
    const objectIds: Set<string> = new Set();
    const materialIds: Set<string> = new Set();

    const splitValue = (value: string) => {
        return value
            ? value
                  .split(';')
                  .map((v) => v.trim())
                  .filter((v) => v)
            : [value];
    };
    nodes.forEach((node) => {
        switch (node.type) {
            case ComposerOptionActionsType.SHOW_OBJECTS:
            case ComposerOptionActionsType.HIDE_OBJECTS:
                if (!node.selector) break;
                splitValue(node.selector).forEach((id) => objectIds.add(id));
                break;
            case ComposerOptionActionsType.SET_MATERIAL_COLOR:
            case ComposerOptionActionsType.SET_MATERIAL_PARAMETER:
                if (!node.selector) break;
                splitValue(node.selector).forEach((id) => materialIds.add(id));
                break;
            case ComposerOptionActionsType.SET_OBJECT_MATERIAL:
            case ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL:
            case ComposerOptionActionsType.SET_DESIGN_MATERIAL:
                if (!node.selector || !node.value) break;
                splitValue(node.selector).forEach((id) => objectIds.add(id));
                splitValue(node.value).forEach((id) => materialIds.add(id));
                break;
            default:
                break;
        }
    });
    return { objectIds: Array.from(objectIds), materialIds: Array.from(materialIds) };
}

// Only object/material-bearing actions carry ids that can be retargeted between products. Other action types
// (SET_PRINTING_METHOD stores a printTypeID, camera actions store camera ids) must be left untouched so their
// value/selector are never accidentally rewritten by the material/object maps.
const RETARGETABLE_ACTION_TYPES = new Set<ComposerOptionActionsType>([
    ComposerOptionActionsType.SHOW_OBJECTS,
    ComposerOptionActionsType.HIDE_OBJECTS,
    ComposerOptionActionsType.SET_OBJECT_MATERIAL,
    ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL,
    ComposerOptionActionsType.SET_DESIGN_MATERIAL,
    ComposerOptionActionsType.SET_MATERIAL_COLOR,
    ComposerOptionActionsType.SET_MATERIAL_PARAMETER
]);

export function retargetMapToActions(
    nodes: ComposerOptionActionNode[],
    materialMap?: Record<string, string>,
    objectMap?: Record<string, string>
): ComposerOptionActionNode[] {
    const remapDelimitedValue = (value: string | undefined, map?: Record<string, string>) => {
        if (!value || !map) {
            return value;
        }
        return value
            .split(';')
            .map((entry) => {
                const trimmed = entry.trim();
                if (!trimmed) {
                    return entry;
                }
                const mapped = map[trimmed];
                if (!mapped) {
                    return entry;
                }
                const leadingWhitespace = entry.match(/^\s*/)?.[0] ?? '';
                const trailingWhitespace = entry.match(/\s*$/)?.[0] ?? '';
                return `${leadingWhitespace}${mapped}${trailingWhitespace}`;
            })
            .join(';');
    };

    return nodes.map((node) => {
        if (!RETARGETABLE_ACTION_TYPES.has(node.type)) {
            return node;
        }

        const remappedSelector = remapDelimitedValue(node.selector, objectMap);
        const remappedValue = remapDelimitedValue(node.value, materialMap);

        return {
            ...node,
            selector: remappedSelector ?? node.selector,
            value: remappedValue ?? node.value
        };
    });
}

export function setActionSceneElementType(action: ComposerOptionActionNode | MPlaza.AttributeSceneAction): ComposerOptionActionNode {
    const isNode = !!(action as ComposerOptionActionNode)?.type;
    const normalizedType =
        (action as ComposerOptionActionNode)?.type ??
        GetActionTypeFromValue((action as MPlaza.AttributeSceneAction).get('actionTypeID'), (action as MPlaza.AttributeSceneAction).get('value') ?? '');

    const changeElementType = (type: MPlaza.SceneElementType) => {
        if (isNode) {
            (action as ComposerOptionActionNode).sceneElementType = type;
        } else {
            (action as MPlaza.AttributeSceneAction).set('sceneElementTypeID', type);
        }
    };
    switch (normalizedType) {
        case ComposerOptionActionsType.SHOW_OBJECTS:
        case ComposerOptionActionsType.HIDE_OBJECTS:
        case ComposerOptionActionsType.SET_DESIGN_MATERIAL:
        case ComposerOptionActionsType.SET_OBJECT_MATERIAL:
            changeElementType(MPlaza.SceneElementType.Mesh);
            break;
        case ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL:
            changeElementType(MPlaza.SceneElementType.SubMesh);
            break;
        case ComposerOptionActionsType.SET_MATERIAL_COLOR:
        case ComposerOptionActionsType.SET_MATERIAL_PARAMETER:
            changeElementType(MPlaza.SceneElementType.Material);
            break;
        case ComposerOptionActionsType.MOVE_CAMERA:
        case ComposerOptionActionsType.SET_CAMERA_PIVOT:
        case ComposerOptionActionsType.RESET_CAMERA_PIVOT:
        case ComposerOptionActionsType.SET_PRINTING_METHOD:
            changeElementType(MPlaza.SceneElementType.None);
            break;
        default:
            break;
    }
    return action as any;
}

/* template & area*/

export function getAreaPath(width: number, height: number) {
    return [
        'Path',
        {
            applyMatrix: true,
            data: {
                guid: '00470596-d7ac-4b82-ebe0-d3b207794bb1',
                isArea: true,
                selected: true
            },
            segments: [
                [-width / 2, height / 2],
                [-width / 2, -height / 2],
                [width / 2, -height / 2],
                [width / 2, height / 2]
            ],
            closed: true,
            strokeColor: 'black',
            strokeWidth: 1,
            dashOffset: 1,
            dashArray: [1.5, 1.5]
        }
    ];
}

export function getAreaSVG(width: number, height: number) {
    return `<path xmlns="http://www.w3.org/2000/svg" d="M-${width / 2},${
        height / 2
    }l0,-${height}l${width},0l0,${height}z" data-paper-data="{&quot;guid&quot;:&quot;00470596-d7ac-4b82-ebe0-d3b207794bb1&quot;,&quot;isArea&quot;:true,&quot;selected&quot;:true}" fill="none" stroke="#000000" stroke-width="1" stroke-linecap="butt" stroke-linejoin="miter" stroke-miterlimit="10" stroke-dasharray="1.5,1.5" stroke-dashoffset="1" font-family="sans-serif" font-weight="normal" font-size="12" text-anchor="start" mix-blend-mode="normal"></path>`;
}
